import type {
  AnnulOperation,
  BranchChangeset,
  ConfigurationValidationError,
} from '@festina/configuration-general-types/configuration'
import { OperationLayerMap, type WorkspaceKey } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { useMemo } from 'react'

import type { ConfigurationItemsByLayer } from '../../workspace/operationsContext'
import type { ValidationErrorWithLocation } from '../errors/types'
import type { WorkspaceOperationOf } from '../types'
import type { EnumDefinitionWithoutLiterals } from './types'
import { useConfiguredEnumDefinitionsFromChangeset } from './useConfiguredEnumDefinitionsFromChangeset'
import { useDraftEnumDefinitionsWithoutLiterals } from './useDraftEnumDefinitionsWithoutLiterals'

export const useConfiguredEnumDefinitionsWithoutLiterals = ({
  configurationChangesets,
  workspaceKey,
  errors,
  draftAnnulOperations,
}: {
  configurationChangesets: Optional<BranchChangeset[]>
  workspaceKey: Optional<WorkspaceKey>
  errors: ValidationErrorWithLocation<ConfigurationValidationError>[]
  draftAnnulOperations?: WorkspaceOperationOf<AnnulOperation>[]
}): Map<string, ConfigurationItemsByLayer<EnumDefinitionWithoutLiterals>> => {
  const draftEnumDefinitions = useDraftEnumDefinitionsWithoutLiterals(
    workspaceKey,
    errors,
  )
  const changesetEnumDefinitions = useConfiguredEnumDefinitionsFromChangeset({
    configurationChangesets,
    errors,
    draftAnnulOperations,
  })

  return useMemo(() => {
    const allDefinitions = [
      ...changesetEnumDefinitions,
      ...draftEnumDefinitions,
    ]

    const enumsMap = new Map<
      string,
      ConfigurationItemsByLayer<EnumDefinitionWithoutLiterals>
    >()

    allDefinitions.forEach((enumDefinition) => {
      const definitionBlock = enumDefinition.block

      const indexedBlock =
        enumsMap.get(definitionBlock) ??
        new OperationLayerMap<Map<string, EnumDefinitionWithoutLiterals>>()
      enumsMap.set(definitionBlock, indexedBlock)

      const enumDefinitions =
        indexedBlock.get(enumDefinition.layer) ?? new Map()
      indexedBlock.set(enumDefinition.layer, enumDefinitions)

      // We implicitly override any enum definitions on the branch with drafts defined in the workspace
      enumDefinitions.set(enumDefinition.name, enumDefinition)
    })

    return enumsMap
  }, [changesetEnumDefinitions, draftEnumDefinitions])
}
